import { useMemo, useSyncExternalStore } from 'react';
import { ConciergeSettings, DEFAULT_CONCIERGE_SETTINGS, mergeConciergeSettings } from './types';

/**
 * Hook to read window.brandclubSettings.concierge with reactivity
 * Re-renders when settings change (via custom event)
 */
export function useConciergeSettings(): Required<ConciergeSettings> {
  // Use useSyncExternalStore for SSR-safe access to window
  const conciergeSettings = useSyncExternalStore(
    // Subscribe function - listen for changes
    (callback) => {
      const handler = () => callback();
      window.addEventListener('brandclub:settings:update', handler);
      return () => window.removeEventListener('brandclub:settings:update', handler);
    },
    // Get snapshot - read current settings
    () => {
      if (typeof window === 'undefined') return null;
      return window.brandclubSettings?.concierge;
    },
    // Get server snapshot - return null for SSR
    () => null
  );

  // Merge with defaults
  return useMemo(() => mergeConciergeSettings(conciergeSettings), [conciergeSettings]);
}

/**
 * Get Concierge settings synchronously (non-reactive)
 * Use this in callbacks or effects where you need the current value
 */
export function getConciergeSettings(): Required<ConciergeSettings> {
  if (typeof window === 'undefined') {
    return DEFAULT_CONCIERGE_SETTINGS;
  }
  return mergeConciergeSettings(window.brandclubSettings?.concierge);
}

/**
 * Update Concierge settings programmatically
 * 
 * @example
 * updateConciergeSettings({ z_index: 9999 });
 */
export function updateConciergeSettings(newSettings: Partial<ConciergeSettings>): void {
  if (typeof window === 'undefined') return;
  
  window.brandclubSettings = {
    ...window.brandclubSettings,
    concierge: {
      ...window.brandclubSettings?.concierge,
      ...newSettings,
    },
  };
  
  // Dispatch event to trigger re-renders
  window.dispatchEvent(new CustomEvent('brandclub:settings:update'));
}

